import React from "react";
import {
  Archive, Trash2, Pencil, Users as UsersIcon, Calendar, Briefcase, Lock,
} from "lucide-react";
import { Badge, Avatar } from "../../components/primitives";
import { IconBtn } from "../../components/RowControls";
import { Shimmer } from "../../components/StatCard";
import { isGeneralProject, fmtDate } from "./projectHelpers";
import {
  identityHue, statusMeta, typeLabel, memberEntries,
  scheduleOf, scheduleColor, goalsOf,
} from "./projectVisuals";

// Column definitions for the projects table view. Kept out of the screen file so
// the screen stays about data and state, and this stays about presentation.
//
// The table is the dense sibling of the grid: same record, same derived values
// (identity colour, status, goal split, schedule), read as rows instead of
// cards. It carries no tracked-hours column — time lives in the project's
// Analytics panel, still reachable from the team avatars and the quick view.
export function projectColumns({
  t, stats, statsLoading, canEdit, canManage,
  onInfo, onEdit, onArchive, onDelete, onMembers, onGoals,
}) {
  const statOf = (p) => stats[String(p._id || p.id)];

  return [
    {
      key: "name", header: "Project",
      render: (p) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, maxWidth: 360 }}>
          <span style={{ width: 3, height: 20, borderRadius: 99, background: identityHue(t, p), flexShrink: 0 }} />
          <span style={{ fontSize: t.font.size.md, fontWeight: 500, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
          {p.code && (
            <span style={{
              fontSize: t.font.size.xs, fontWeight: 500, padding: "1px 6px", borderRadius: t.r.xs,
              background: t.c.surfaceMuted, color: t.c.textTertiary, border: `1px solid ${t.c.border}`, flexShrink: 0,
            }}>{p.code}</span>
          )}
          {isGeneralProject(p) && <Lock size={12} style={{ color: t.c.textTertiary, flexShrink: 0 }} title="System project" />}
        </div>
      ),
    },
    {
      key: "status", header: "Status", width: 124,
      render: (p) => {
        const st = statusMeta(p.status);
        return <Badge tone={st.key} dot>{st.label}</Badge>;
      },
    },
    {
      key: "type", header: "Type", width: 116,
      render: (p) => {
        const label = typeLabel(p);
        return label
          ? <span style={{ color: t.c.textSecondary }}>{label}</span>
          : <span style={{ color: t.c.textTertiary }}>—</span>;
      },
    },
    {
      key: "members", header: "Team", width: 130,
      render: (p) => {
        const members = memberEntries(p);
        if (!members.length) {
          return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: t.font.size.xs, color: t.c.textTertiary }}>
              <UsersIcon size={13} /> None
            </span>
          );
        }
        return (
          <button onClick={(e) => { e.stopPropagation(); onMembers(p); }} title={`Member hours on ${p.name}`}
            style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: "transparent", cursor: "pointer", padding: 0 }}>
            {members.slice(0, 4).map((m, i) => (
              <span key={m.key} style={{ marginLeft: i ? -8 : 0, border: `2px solid ${t.c.surface}`, borderRadius: 99, display: "inline-flex" }}>
                <Avatar user={m.user} name={m.name} size={24} />
              </span>
            ))}
            {members.length > 4 && <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginLeft: 4 }}>+{members.length - 4}</span>}
          </button>
        );
      },
    },
    {
      key: "goals", header: "Goals", width: 150,
      render: (p) => {
        const s = statOf(p);
        if (statsLoading && !s) return <Shimmer w={84} h={10} />;
        const g = goalsOf(s);
        if (!g) return <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>None</span>;
        return (
          <button onClick={(e) => { e.stopPropagation(); onGoals(p); }} title={`${g.approved} approved · ${g.pending} pending on ${p.name}`}
            style={{ display: "flex", alignItems: "center", gap: 8, border: "none", background: "transparent", cursor: "pointer", padding: 0 }}>
            <span style={{ display: "flex", width: 74, height: 5, borderRadius: 99, overflow: "hidden", background: t.c.surfaceMuted, gap: 1.5 }}>
              <span style={{ width: `${(g.approved / g.total) * 100}%`, background: t.status.positive }} />
              <span style={{ width: `${(g.pending / g.total) * 100}%`, background: t.status.warning, opacity: 0.5 }} />
            </span>
            <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
              {g.approved}/{g.total}
            </span>
          </button>
        );
      },
    },
    {
      key: "endDate", header: "Due", width: 140,
      render: (p) => {
        const sched = scheduleOf(p);
        if (sched.kind === "none" || sched.kind === "system") return <span style={{ color: t.c.textTertiary }}>—</span>;
        // Colour matches the card's schedule bar for the same project, so the
        // two views never disagree about whether something is late.
        return (
          <span title={sched.right} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: t.font.size.sm, color: scheduleColor(t, sched), whiteSpace: "nowrap" }}>
            <Calendar size={12} /> {fmtDate(sched.end)}
          </span>
        );
      },
    },
    {
      key: "actions", header: "", align: "right", width: 140,
      render: (p) => {
        const isGeneral = isGeneralProject(p);
        return (
          <div className="tg-row-actions" style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            <IconBtn title="Quick view" onClick={() => onInfo(p)}><Briefcase size={15} /></IconBtn>
            {canEdit && <IconBtn title="Edit" onClick={() => onEdit(p)}><Pencil size={15} /></IconBtn>}
            {canManage && !isGeneral && <IconBtn title={p.status === "archived" ? "Unarchive" : "Archive"} onClick={() => onArchive(p)}><Archive size={15} /></IconBtn>}
            {canManage && !isGeneral && <IconBtn title="Delete" danger onClick={() => onDelete(p)}><Trash2 size={15} /></IconBtn>}
          </div>
        );
      },
    },
  ];
}
